import React, { useEffect } from 'react'
import { Box, DialogContent, Stack, Typography } from '@mui/material'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { TFunction } from 'i18next'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { EvseBrand, evseDevicesConfig, EvseReference, getDeviceDefaultName, getEvseCategory } from 'src/Libs/flow/parsers'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { InfoIcon } from 'src/Libs/flow/icons'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import Actions from '@e3dc-react/shell/Elements/Dialog/ActionDialog/Actions'
import { useDialogActionsPositioned } from '../../../hooks'
import {
  cintechReference,
  DeviceReference,
  DeviceReferences,
  eebusReference,
  wittyFlexReference,
  wittyLinkReference,
} from 'src/Libs/wallboxes/models/wallbox-reference'

interface AddChargingStationForm {
  name: string
  brand: EvseBrand
  hagerDeviceType: DeviceReference
  audiDeviceType: '8V4, 9Y0'
}

//#region Main Component

interface AddChargingStationProps {
  error: string | null
  closeDialog: () => void
  addWallbox: (name: string, reference: EvseReference) => void

  chargingStations: InstallationFormModel['chargingStations']
}

/**
 * Form to add a charging station to the installation. Thought of as content inside a dialog.
 */
export const AddChargingStation: React.FC<AddChargingStationProps> = ({ error, closeDialog, addWallbox, chargingStations }) => {
  const { t } = useCustomTranslation()

  const {
    featureFlags: { useEvseWittyLinkSubTypes, maxWittyLinksPerInstallation, useEvseWittyFlex },
  } = useFeatureFlagsContext()

  const { modbusTcp, eebus } = chargingStations

  const amountOfWittyLinks = modbusTcp.filter(({ reference }) => evseDevicesConfig[reference].type === 'WittyLink').length

  const hagerDeviceTypeOptions = hagerDeviceOptions(t, useEvseWittyLinkSubTypes, amountOfWittyLinks < maxWittyLinksPerInstallation, useEvseWittyFlex)

  const methods = useForm<AddChargingStationForm>({
    defaultValues: {
      name: t(evseDevicesConfig[wittyLinkReference].defaultNameTranslationKey),
      brand: 'Hager',
      audiDeviceType: '8V4, 9Y0',
      hagerDeviceType: hagerDeviceTypeOptions.find(option => !option.disabled)!.value as AddChargingStationForm['hagerDeviceType'],
    },
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const { control, handleSubmit, formState, setValue } = methods

  const [brand, hagerDeviceType, audiDeviceType] = useWatch({
    control,
    name: ['brand', 'hagerDeviceType', 'audiDeviceType'],
  })

  const reference: EvseReference = brand === 'Audi' ? audiDeviceType : hagerDeviceType
  const isEebus = getEvseCategory(reference) === 'EEBus'
  const deviceType = brand === 'Audi' ? audiDeviceType : hagerDeviceType
  const { defaultNameTranslationKey, category } = evseDevicesConfig[deviceType]
  const wallboxIndex = category === 'ModbusTCP' ? modbusTcp.length + 1 : eebus.length + 1

  useEffect(() => {
    setValue('name', t(defaultNameTranslationKey, '', { wallboxIndex }), { shouldDirty: true })
  }, [defaultNameTranslationKey, wallboxIndex, setValue, t])

  const onSubmit: SubmitHandler<AddChargingStationForm> = data => {
    const { name, brand, hagerDeviceType, audiDeviceType } = data
    if (brand === 'Audi') {
      addWallbox(name, audiDeviceType)
    } else {
      addWallbox(name, hagerDeviceType)
    }
    closeDialog()
  }

  const isSaveButtonEnabled = !error && formState.isValid

  return (
    <FormProvider {...methods}>
      <Box component={'form'} display={'contents'} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers sx={{ px: 0, py: 4 }}>
          {error ? (
            <Box>
              <Alert severity="warning" sx={{ marginBottom: 2 }} text={error} />
              <Stack gap={2}>
                {eebus.map(evse => (
                  <ShellTextField
                    key={evse.id}
                    label={t('common.name')}
                    value={getDeviceDefaultName(evse.name, 'EEBus Evse', evse.deviceId, t)}
                    disabled
                  />
                ))}
                {modbusTcp.map(evse => (
                  <ShellTextField
                    key={evse.id}
                    label={t('common.name')}
                    value={getDeviceDefaultName(evse.name, 'ModbusTCP Evse', evse.deviceId, t)}
                    disabled
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField name="name" label={t('installation.productEditor.deviceName')} required fullWidth />
              <Select name="brand" label={t('installation.productEditor.brand')} options={brandOptions(useEvseWittyLinkSubTypes)} fullWidth />
              {brand === 'Hager' && (
                <Select name="hagerDeviceType" label={t('installation.productEditor.reference')} options={hagerDeviceTypeOptions} fullWidth />
              )}
              {brand === 'Audi' && (
                <Select name="audiDeviceType" label={t('installation.productEditor.reference')} options={audiDeviceOptions(t)} fullWidth />
              )}
              {isEebus && (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                  <InfoIcon />
                  <Typography variant="body2">{t('installation.evseEditor.pairingInfo')}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <Actions
          actions={useDialogActionsPositioned(t('installation.productEditor.edit.evse'), handleSubmit(onSubmit), !isSaveButtonEnabled, closeDialog)}
        />
      </Box>
    </FormProvider>
  )
}

// #region Helpers

const validationSchema = z.object({
  name: z.string().nonempty(),
  brand: z.enum(['Hager', 'Audi']),
  hagerDeviceType: z.enum(DeviceReferences).optional(),
  audiDeviceType: z.enum(['8V4, 9Y0']).optional(),
})

const brandOptions = (useWittyLinkOptions: boolean): Options<EvseBrand> => [
  { value: 'Hager', label: 'Hager' },
  ...(useWittyLinkOptions ? [{ value: 'Audi', label: 'Audi' } as const] : []),
]
const hagerDeviceOptions = (
  t: TFunction,
  useWittyLinkOptions: boolean,
  wittyLinkIsEnabled: boolean,
  useWittyFlexOptions: boolean
): Options<EvseReference> => [
  ...(useWittyLinkOptions
    ? [
        {
          value: wittyLinkReference,
          label: `${t('installation.evseEditor.TcpRef.wittySolarV2AttachedCab')} (ModbusTCP)`,
          disabled: !wittyLinkIsEnabled,
        } as const,
      ]
    : []),
  {
    value: cintechReference,
    label: `${t('installation.evseEditor.TcpRef.wittySolarV2Sock')} (ModbusTCP)`,
  },
  { value: eebusReference, label: `${t('installation.evseEditor.EebusRef.wittyflow')} (EEBus)` },
  ...(useWittyFlexOptions ? [{ value: wittyFlexReference, label: t('installation.evseEditor.TcpRef.wittyFlex') } as const] : []),
]
const audiDeviceOptions = (t: TFunction): Options<EvseReference> => [
  { value: '8V4, 9Y0', label: `${t('installation.evseEditor.EebusRef.audi')} (EEBus)` },
]
